import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { AlertTriangle, ShieldAlert } from 'lucide-react';
import { formatPHP } from '../lib/utils';

const REASONS = [
  { key: 'clearance',         label: 'Clearance / Old stock' },
  { key: 'damaged',           label: 'Damaged goods' },
  { key: 'owner_approved',    label: 'Owner approved promo' },
  { key: 'competitor_match',  label: 'Competitor price match' },
  { key: 'other',             label: 'Other (specify)' },
];

/**
 * BelowCapitalModal — fires at checkout when any item is priced at or below
 * the branch capital. Requires manager/admin PIN + a logged reason.
 *
 * Props:
 *   open            : boolean
 *   items           : [{ product_name, price, capital, margin }]
 *   onConfirm({ pin, reason, reason_detail }): called after validation
 *   onCancel()      : called on dismiss
 *   submitting      : boolean
 *   error           : string — back-end error (e.g. wrong PIN)
 */
export default function BelowCapitalModal({
  open, items = [], onConfirm, onCancel, submitting = false, error = '',
}) {
  const [pin, setPin]               = useState('');
  const [reason, setReason]         = useState('owner_approved');
  const [reasonDetail, setDetail]   = useState('');
  const [localErr, setLocalErr]     = useState('');

  // Reset on open
  useEffect(() => {
    if (open) { setPin(''); setReason('owner_approved'); setDetail(''); setLocalErr(''); }
  }, [open]);

  if (!open) return null;

  const losses  = items.filter(i => i.margin < 0);
  const zeroes  = items.filter(i => i.margin === 0);

  const handleConfirm = () => {
    setLocalErr('');
    if (!pin.trim()) { setLocalErr('Owner or Admin PIN / TOTP required'); return; }
    if (reason === 'other' && !reasonDetail.trim()) {
      setLocalErr('Please specify the reason'); return;
    }
    onConfirm({ pin: pin.trim(), reason, reason_detail: reasonDetail.trim() });
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !submitting) onCancel(); }}>
      <DialogContent className="max-w-lg" data-testid="below-capital-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-red-700">
            <ShieldAlert size={18} /> Authorization Required — At/Below Capital
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            {items.length} item{items.length !== 1 ? 's' : ''} on this receipt {items.length === 1 ? 'is' : 'are'} priced at or below your capital cost.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4 py-1">

          {/* Warning banner */}
          <div className="flex items-start gap-2.5 rounded-lg bg-red-50 border border-red-200 p-3">
            <AlertTriangle size={16} className="text-red-500 shrink-0 mt-0.5" />
            <p className="text-xs text-red-800 leading-relaxed">
              You are selling at or below cost.{' '}
              <strong>If you are not authorized, stop and call your admin or owner.</strong>{' '}
              This action will be logged with the authorizing PIN.
            </p>
          </div>

          {/* Item breakdown */}
          <div className="border border-slate-200 rounded-lg overflow-hidden text-xs">
            <div className="bg-slate-50 px-3 py-1.5 grid grid-cols-4 gap-2 font-semibold text-slate-500 uppercase tracking-wide text-[10px]">
              <span className="col-span-2">Product</span>
              <span className="text-right">Sale / Capital</span>
              <span className="text-right">Margin</span>
            </div>
            <div className="divide-y divide-slate-100 max-h-40 overflow-y-auto">
              {items.map((it, i) => {
                const isLoss = it.margin < 0;
                return (
                  <div key={i}
                    className={`px-3 py-2 grid grid-cols-4 gap-2 items-center ${isLoss ? 'bg-red-50/60' : 'bg-amber-50/60'}`}
                    data-testid={`below-cap-item-${i}`}>
                    <span className="col-span-2 font-medium text-slate-800 truncate" title={it.product_name}>
                      {it.product_name}
                    </span>
                    <div className="text-right">
                      <p className={`font-bold ${isLoss ? 'text-red-700' : 'text-amber-700'}`}>
                        {formatPHP(it.price)}
                      </p>
                      <p className="text-slate-400 text-[10px]">cap {formatPHP(it.capital)}</p>
                    </div>
                    <div className="text-right">
                      <span className={`inline-block px-1.5 py-0.5 rounded text-[10px] font-bold ${
                        isLoss ? 'bg-red-100 text-red-700' : 'bg-amber-100 text-amber-700'
                      }`}>
                        {isLoss ? `LOSS ${formatPHP(it.margin)}` : 'ZERO MARGIN'}
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Summary chips */}
          {(losses.length > 0 || zeroes.length > 0) && (
            <div className="flex gap-2 flex-wrap">
              {losses.length > 0 && (
                <span className="text-[11px] bg-red-100 text-red-700 rounded-full px-2.5 py-0.5 font-semibold">
                  {losses.length} item{losses.length !== 1 ? 's' : ''} sold at a loss
                </span>
              )}
              {zeroes.length > 0 && (
                <span className="text-[11px] bg-amber-100 text-amber-700 rounded-full px-2.5 py-0.5 font-semibold">
                  {zeroes.length} item{zeroes.length !== 1 ? 's' : ''} at zero margin
                </span>
              )}
            </div>
          )}

          {/* Reason */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              Reason <span className="text-red-500">*</span>
            </label>
            <div className="grid grid-cols-2 gap-1.5">
              {REASONS.map(r => (
                <button
                  key={r.key}
                  onClick={() => setReason(r.key)}
                  data-testid={`below-cap-reason-${r.key}`}
                  className={`text-xs px-3 py-2 rounded-md border text-left transition-colors ${
                    reason === r.key
                      ? 'bg-red-600 text-white border-red-600'
                      : 'border-slate-200 hover:border-red-300 hover:bg-red-50 text-slate-700'
                  }`}>
                  {r.label}
                </button>
              ))}
            </div>
            {reason === 'other' && (
              <input
                value={reasonDetail}
                onChange={e => setDetail(e.target.value)}
                placeholder="Specify reason…"
                className="mt-2 w-full text-xs border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300"
                data-testid="below-cap-reason-detail"
              />
            )}
          </div>

          {/* PIN */}
          <div>
            <label className="text-xs font-semibold text-slate-600 block mb-1">
              Owner / Admin PIN or TOTP <span className="text-red-500">*</span>
            </label>
            <input
              type="password" autoComplete="one-time-code" data-lpignore="true"
              autoComplete="one-time-code"
              data-lpignore="true"
              value={pin}
              onChange={e => { setPin(e.target.value); setLocalErr(''); }}
              onKeyDown={e => e.key === 'Enter' && handleConfirm()}
              placeholder="Admin PIN or Google Authenticator code"
              autoFocus
              className="w-full text-sm border border-slate-200 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-red-300 font-mono tracking-widest"
              data-testid="below-cap-pin-input"
            />
          </div>

          {(localErr || error) && (
            <p className="text-xs text-red-600 flex items-center gap-1.5">
              <AlertTriangle size={12} /> {localErr || error}
            </p>
          )}

          <div className="flex gap-2 pt-1 border-t">
            <Button variant="outline" className="flex-1" onClick={onCancel} disabled={submitting}
              data-testid="below-cap-cancel-btn">
              Cancel
            </Button>
            <Button
              onClick={handleConfirm}
              disabled={submitting || !pin.trim()}
              className="flex-1 bg-red-600 hover:bg-red-700 text-white"
              data-testid="below-cap-confirm-btn">
              {submitting ? 'Authorizing…' : 'Authorize Sale'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
